import styled from "@emotion/styled";
import { Button, Progress } from "antd";
import { useNavigate } from "react-router-dom";
import { SellerReportPeriod } from "services/interface/sellerReport.interface";
import { paths } from "setup/PageRouter/routesConfig";
import { numberFormatInt } from "utils/nuberFormat";
import { useSellerCustomerBase } from "hooks/useSellerReport";
import DeltaText from "../shared/DeltaText";
import SectionCard from "../shared/SectionCard";
import { sd } from "../tokens";

interface RepeatCustomerCardProps {
  period: SellerReportPeriod;
}

/** เพดานของแถบ "ลูกค้าใหม่" — ไม่มีตัวหารจาก API จึงสเกลเป็นช่วง ๆ ให้แถบยังอ่านออก */
const progressPercent = (count: number): number => {
  if (count <= 0) return 0;
  const ceiling = Math.max(10, Math.pow(10, Math.ceil(Math.log10(count + 1))));
  return Math.min(100, (count / ceiling) * 100);
};

const RepeatCustomerCard = ({ period }: RepeatCustomerCardProps) => {
  const navigate = useNavigate();
  const { data, isPending, isFetching, isError, refetch } = useSellerCustomerBase(period);

  const repeatPct = data?.repeat_customer_pct ?? 0;
  const newCustomers = data?.new_customer_count ?? 0;

  return (
    <SectionCard
      eyebrow="ฐานลูกค้า"
      title="โอกาสซื้อซ้ำ"
      minHeight={280}
      skeletonRows={4}
      loading={isPending}
      refreshing={isFetching && !isPending}
      error={isError ? true : undefined}
      onRetry={() => refetch()}
    >
      <Layout>
        <TopRow>
          <BigValue>{repeatPct.toFixed(0)}%</BigValue>
          <TopText>
            <Caption>ลูกค้ากลับมาซื้อซ้ำ</Caption>
            <DeltaText value={data?.repeat_customer_change_pct ?? 0} caption="" />
          </TopText>
        </TopRow>

        <NewCustomer>
          <NewCustomerHead>
            <span>ลูกค้าใหม่</span>
            <strong>{numberFormatInt(newCustomers)}</strong>
          </NewCustomerHead>
          <Progress
            percent={progressPercent(newCustomers)}
            showInfo={false}
            strokeColor={sd.purple}
            trailColor={sd.border}
            size="small"
          />
        </NewCustomer>

        <Button block onClick={() => navigate(paths.manageAgent)}>
          ดูฐานลูกค้าของฉัน →
        </Button>
      </Layout>
    </SectionCard>
  );
};

export default RepeatCustomerCard;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const BigValue = styled.div`
  font-size: 34px;
  font-weight: 700;
  line-height: 1.1;
  color: ${sd.purple};
`;

const TopText = styled.div`
  min-width: 0;
`;

const Caption = styled.div`
  font-size: 13px;
  color: ${sd.muted};
  margin-bottom: 2px;
`;

const NewCustomer = styled.div`
  margin-top: auto;
`;

const NewCustomerHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: ${sd.muted};
  margin-bottom: 4px;

  strong {
    color: ${sd.ink};
  }
`;
